// `configureVoiceBuildMode` — the build-mode seam for the voice surfaces.
//
// A package may not read `process.env` (`manifest-boundary.test.ts` scans every
// source file for it), and three voice modules did: `debug/formatVoiceDebugDump`
// and `workspace/useWorkspaceCanvasActions` read it directly, and
// `workspace/VoiceWorkspaceDevTrigger` is gated on a read its parent performs.
// What they want is not deployment configuration in the `configureAgentsData`
// sense — no origin, no header, no token — but the two BUILD-MODE booleans that
// decide whether a dev-only affordance exists at all, so they get their own
// seam rather than a widening of that one.
//
// RESOLVED BOOLEANS, not the env names. `PlatformConfig` in
// `@heidi/runtime/platform` is the precedent: the app reads its own env and
// passes the answer, so the package never encodes what `NEXT_PUBLIC_ENVIRONMENT`
// is allowed to contain. Values rather than thunks, because neither signal can
// change while the app runs — the flag rule that forces a `() => boolean` is
// about a value that flips, and a build mode does not.
//
// THE DEFAULT IS THE PRODUCTION ANSWER, and it does not throw. Every other
// getter in this package fails loudly on unconfigured use, because a missing
// origin or token is a broken request. Here the caller is asking whether to
// offer a debug affordance, so the safe answer to "nobody configured me" is
// "no" — a storybook, a jest suite or an early module-scope read must never be
// the thing that turns a session dump on.

export interface VoiceBuildMode {
  /**
   * App-side "not a production build" signal — the wiring passes
   * `process.env.NODE_ENV !== 'production'`. Gates the workspace demo paths
   * (quick-start chips, follow-ups) and the dev-only workspace trigger.
   */
  isDevBuild: boolean;
  /**
   * App-side "this deployment may keep a full session dump" signal — the wiring
   * passes the local/dev environment test. Distinct from `isDevBuild`: a
   * production BUILD served from a dev deployment still dumps, and that
   * asymmetry is the whole reason there are two fields.
   */
  isDebugDumpEnvironment: boolean;
}

const PRODUCTION: VoiceBuildMode = { isDevBuild: false, isDebugDumpEnvironment: false };

let buildMode: VoiceBuildMode = PRODUCTION;

export const configureVoiceBuildMode = (next: VoiceBuildMode): void => {
  buildMode = next;
};

export const voiceBuildMode = (): VoiceBuildMode => buildMode;

/** Test-only reset so a suite cannot leak its build mode into the next one. */
export const resetVoiceBuildModeForTests = (): void => {
  buildMode = PRODUCTION;
};
